import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { MessageSquare, Phone, Check, X, Loader2, Send, Trash2, AlertCircle, Copy, Shield, Star, StarOff, Plus, Globe, Smartphone, ChevronDown, ChevronUp, MoreVertical } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface WhatsAppNumber {
  id?: number;
  phoneNumber?: string;
  countryCode?: string;
  fullNumber?: string;
  verified?: boolean;
  verificationCode?: string;
  verificationSentAt?: string;
  createdAt?: string;
  menuId?: number;
  isPrimary?: boolean;
  whatsappNumber?: string; // Add this field from your backend
}

interface WhatsAppManagerProps {
  menuId?: string | number;
}

// Country codes for dropdown
const COUNTRY_CODES = [
  { code: '+39', name: 'Italy', flag: '🇮🇹' },
  { code: '+33', name: 'France', flag: '🇫🇷' },
  { code: '+34', name: 'Spain', flag: '🇪🇸' },
  { code: '+49', name: 'Germany', flag: '🇩🇪' },
  { code: '+44', name: 'UK', flag: '🇬🇧' },
  { code: '+1', name: 'USA/Canada', flag: '🇺🇸' },
  { code: '+61', name: 'Australia', flag: '🇦🇺' },
  { code: '+55', name: 'Brazil', flag: '🇧🇷' },
  { code: '+91', name: 'India', flag: '🇮🇳' },
  { code: '+86', name: 'China', flag: '🇨🇳' },
  { code: '+81', name: 'Japan', flag: '🇯🇵' },
  { code: '+82', name: 'South Korea', flag: '🇰🇷' },
  { code: '+41', name: 'Switzerland', flag: '🇨🇭' },
  { code: '+31', name: 'Netherlands', flag: '🇳🇱' },
  { code: '+32', name: 'Belgium', flag: '🇧🇪' },
  { code: '+46', name: 'Sweden', flag: '🇸🇪' },
  { code: '+47', name: 'Norway', flag: '🇳🇴' },
  { code: '+45', name: 'Denmark', flag: '🇩🇰' },
  { code: '+358', name: 'Finland', flag: '🇫🇮' },
  { code: '+353', name: 'Ireland', flag: '🇮🇪' },
];

const WhatsAppManager: React.FC<WhatsAppManagerProps> = ({ menuId }) => {
  const { t } = useTranslation();
  const [phoneNumbers, setPhoneNumbers] = useState<WhatsAppNumber[]>([]);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+39'); // Default to Italy
  const [verificationCode, setVerificationCode] = useState('');
  const [selectedPhone, setSelectedPhone] = useState<WhatsAppNumber | null>(null);
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);
  const [whatsappDedicatedNumber, setWhatsappDedicatedNumber] = useState<string>('+12185795457');
  const [expandedPhoneIndex, setExpandedPhoneIndex] = useState<number | null>(null);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  useEffect(() => {
    if (menuId) {
      fetchPhoneNumbers();
      fetchDedicatedNumber();
    }
  }, [menuId]);

  const fetchPhoneNumbers = async () => {
    if (!menuId) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/menunum/${menuId}/whatsapp`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // SAFELY process the response data with null checks
        const data = response.data.data || [];
        const processedNumbers = data.map((item: any) => ({
          id: item?.id || 0,
          phoneNumber: item?.phoneNumber || item?.whatsappNumber || '',
          countryCode: item?.countryCode || '+39',
          fullNumber: item?.fullNumber || item?.whatsappNumber || item?.phoneNumber || '',
          verified: Boolean(item?.verified),
          menuId: item?.menuId || Number(menuId),
          isPrimary: Boolean(item?.isPrimary),
          createdAt: item?.createdAt || new Date().toISOString(),
          whatsappNumber: item?.whatsappNumber || item?.fullNumber || item?.phoneNumber || ''
        }));
        setPhoneNumbers(processedNumbers);
      } else {
        setPhoneNumbers([]);
      }
    } catch (error: any) {
      console.error('Failed to fetch phone numbers:', error);
      // Use empty array if API fails
      setPhoneNumbers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDedicatedNumber = async () => {
    if (!menuId) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/menunum/${menuId}/whatsapp/dedicated-number`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setWhatsappDedicatedNumber(response.data.data?.dedicatedNumber || '+12185795457');
      } else {
        setWhatsappDedicatedNumber('+12185795457');
      }
    } catch (error) {
      console.error('Failed to fetch dedicated number:', error);
      // Default number if API fails
      setWhatsappDedicatedNumber('+12185795457');
    }
  };

  // SAFE formatPhoneNumber function with null checks
  const formatPhoneNumber = (phone: string | undefined | null): string => {
    if (!phone) return '';
    
    try {
      // Ensure phone is a string
      const phoneStr = String(phone);
      // Remove all non-digit characters
      const cleaned = phoneStr.replace(/\D/g, '');
      
      // Format for display (Italian format as example)
      if (cleaned.length === 10 && phoneStr.includes('+39')) {
        // Format: +39 345 67 89 012
        return `+39 ${cleaned.slice(0, 3)} ${cleaned.slice(3, 5)} ${cleaned.slice(5, 7)} ${cleaned.slice(7, 10)}`;
      } else if (cleaned.length === 10) {
        // Generic 10-digit format
        return `+${cleaned.slice(0, 1)} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7, 10)}`;
      }
      
      // Return original if no specific format
      return phoneStr;
    } catch (error) {
      console.warn('Error formatting phone number:', phone, error);
      return phone || '';
    }
  };

const validatePhoneNumber = (phone: string): { isValid: boolean; message?: string } => {
    if (!phone) return { isValid: false, message: t('whatsapp.manager.phone_number_required') };
    
    try {
      // Remove all non-digit characters
      const digitsOnly = phone.replace(/\D/g, '');
      
      // Check based on country code
      switch (countryCode) {
        case '+39': // Italy: 10 digits starting with 3
          if (digitsOnly.length !== 10) {
            return { isValid: false, message: t('whatsapp.manager.italian_numbers_10_digits') };
          }
          if (!digitsOnly.startsWith('3')) {
            return { isValid: false, message: t('whatsapp.manager.italian_mobile_start_with_3') };
          }
          break;
        case '+1': // USA/Canada: 10 digits
          if (digitsOnly.length !== 10) {
            return { isValid: false, message: t('whatsapp.manager.us_canada_numbers_10_digits') };
          }
          break;
        case '+44': // UK: 10-11 digits
          if (digitsOnly.length < 10 || digitsOnly.length > 11) {
            return { isValid: false, message: t('whatsapp.manager.uk_numbers_10_11_digits') };
          }
          break;
        default:
          // General validation: 5-15 digits
          if (digitsOnly.length < 5 || digitsOnly.length > 15) {
            return { isValid: false, message: t('whatsapp.manager.phone_number_5_15_digits') };
          }
          break;
      }
      
      return { isValid: true };
    } catch (error) {
      console.warn('Error validating phone number:', phone, error);
      return { isValid: false, message: t('whatsapp.manager.invalid_phone_format') };
    }
  };

  const sendCodeRequest = async (phone: string, code: string, forceReassign = false) => {
    const token = localStorage.getItem('token');
    const digitsOnly = phone.replace(/\D/g, '');
    const fullNum = `${code}${digitsOnly}`;

    const response = await axios.post(
      `/menunum/${menuId}/whatsapp/send-code`,
      { 
        phoneNumber: phone,
        countryCode: code,
        isPrimary: phoneNumbers.length === 0,
        forceReassign
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    if (response.data.success) {
      const newNumber: WhatsAppNumber = {
        phoneNumber: phone,
        countryCode: code,
        fullNumber: fullNum,
        verified: false,
        menuId: Number(menuId),
        isPrimary: phoneNumbers.length === 0,
        createdAt: new Date().toISOString(),
        whatsappNumber: fullNum
      };
      setPhoneNumbers([...phoneNumbers, newNumber]);
      setSelectedPhone(newNumber);
      setNewPhoneNumber('');
      toast.success(t('whatsapp.manager.messages.verification_code_sent'));
    }
  };

  const handleAddPhoneNumber = async () => {
    if (!newPhoneNumber.trim()) {
      toast.error(t('whatsapp.manager.messages.enter_phone_number'));
      return;
    }

    const validation = validatePhoneNumber(newPhoneNumber);
    if (!validation.isValid) {
      toast.error(validation.message || t('whatsapp.manager.please_enter_valid_phone'));
      return;
    }

    // Check if number already exists locally
    const digitsOnly = newPhoneNumber.replace(/\D/g, '');
    const fullNumber = `${countryCode}${digitsOnly}`;
    const exists = phoneNumbers.some(num => {
      const numDigits = (num.phoneNumber || '').replace(/\D/g, '');
      const numFull = (num.fullNumber || '').replace(/\D/g, '');
      return numDigits === digitsOnly || numFull === fullNumber.replace(/\D/g, '');
    });
    
    if (exists) {
      toast.error(t('whatsapp.manager.messages.number_already_registered'));
      return;
    }

    setSendingCode(true);
    try {
      await sendCodeRequest(newPhoneNumber, countryCode, false);
    } catch (error: any) {
      console.error('Failed to send code:', error);

      // Handle conflict: number already linked to another menu
      if (error.response?.status === 409 && error.response?.data?.conflict) {
        const linkedMenu = error.response.data.linkedMenu;
        const menuName = linkedMenu?.title || `Menu #${linkedMenu?.id}`;
        const confirmed = window.confirm(
          t('whatsapp.manager.messages.number_linked_warning', 
            `This number is already linked to "${menuName}". Do you want to remove it from "${menuName}" and link it to this menu instead?`)
            .replace('{{menuName}}', menuName)
        );
        if (confirmed) {
          try {
            await sendCodeRequest(newPhoneNumber, countryCode, true);
          } catch (retryError: any) {
            console.error('Failed to reassign number:', retryError);
            toast.error(retryError.response?.data?.error || 'Failed to reassign number');
          }
        }
        // If cancelled, do nothing — number stays with previous menu
      } else {
        const errorMessage = error.response?.data?.error || 'Failed to send verification code';
        const twilioError = error.response?.data?.twilioError;
        if (twilioError) {
          toast.error(`${errorMessage} - ${twilioError}`);
        } else {
          toast.error(errorMessage);
        }
      }
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!selectedPhone || !verificationCode.trim()) {
      toast.error(t('whatsapp.manager.verification_code_required'));
      return;
    }

    setVerifying(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/menunum/${menuId}/whatsapp/verify`,
        {
          phoneNumber: selectedPhone.fullNumber || selectedPhone.phoneNumber || '',
          code: verificationCode
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setPhoneNumbers(phoneNumbers.map(p => {
          const currentFull = p.fullNumber || '';
          const selectedFull = selectedPhone.fullNumber || '';
          return currentFull === selectedFull
            ? { ...p, verified: true }
            : p;
        }));
        setSelectedPhone(null);
        setVerificationCode('');
        toast.success(t('whatsapp.manager.phone_number_verified_success'));
      }
    } catch (error: any) {
      console.error('Verification error:', error);
      toast.error(error.response?.data?.error || t('whatsapp.manager.messages.invalid_verification_code'));
    } finally {
      setVerifying(false);
    }
  };

  const handleDeletePhoneNumber = async (fullNumber: string) => {
    if (!window.confirm(t('whatsapp.manager.confirm_remove_phone'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `/menunum/${menuId}/whatsapp/${encodeURIComponent(fullNumber)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPhoneNumbers(phoneNumbers.filter(p => (p.fullNumber || '') !== fullNumber));
      if (selectedPhone?.fullNumber === fullNumber) {
        setSelectedPhone(null);
      }
      toast.success(t('whatsapp.manager.phone_number_removed'));
    } catch (error: any) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.error || t('whatsapp.manager.failed_remove_phone'));
    }
  };

  const handleResendCode = async (fullNumber: string) => {
    setSendingCode(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `/menunum/${menuId}/whatsapp/resend-code`,
        { phoneNumber: fullNumber },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(t('whatsapp.manager.verification_code_resent'));
    } catch (error: any) {
      console.error('Resend error:', error);
      toast.error(error.response?.data?.error || t('whatsapp.manager.failed_resend_code'));
    } finally {
      setSendingCode(false);
    }
  };

  const setAsPrimary = async (fullNumber: string) => {
    if (!menuId) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `/menunum/${menuId}/whatsapp/set-primary`,
        { phoneNumber: fullNumber },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Update all numbers: set this one as primary, others as not primary
        setPhoneNumbers(prev => 
          prev.map(num => ({
            ...num,
            isPrimary: (num.fullNumber || '') === fullNumber
          }))
        );
        toast.success(t('whatsapp.manager.messages.primary_number_updated'));
      }
    } catch (error: any) {
      console.error('Failed to set primary number:', error);
      toast.error(error.response?.data?.error || t('whatsapp.manager.failed_update_primary'));
    }
  };

  const copyToClipboard = (text: string, message: string = t('whatsapp.manager.copied_to_clipboard')) => {
    if (!text) {
      toast.error(t('whatsapp.manager.nothing_to_copy'));
      return;
    }
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success(message);
  };

  const generateWhatsAppLink = (phoneNumber: string | undefined) => {
    if (!phoneNumber) return '#';
    const cleaned = phoneNumber.replace(/\D/g, '');
    return `https://wa.me/${cleaned}?text=Ciao, vorrei informazioni sul menu.`;
  };

  const openWhatsAppChat = (phoneNumber: string | undefined) => {
    if (!phoneNumber) {
      toast.error(t('whatsapp.manager.invalid_phone_number'));
      return;
    }
    const link = generateWhatsAppLink(phoneNumber);
    window.open(link, '_blank');
  };

  const copyDedicatedNumber = () => {
    copyToClipboard(whatsappDedicatedNumber, t('whatsapp.manager.dedicated_number_copied'));
  };

  // Render functions with safe checks
  const renderPhoneNumber = (phone: WhatsAppNumber) => {
    const displayNumber = formatPhoneNumber(phone.fullNumber || phone.phoneNumber || phone.whatsappNumber);
    return displayNumber || 'N/A';
  };

  const renderDate = (dateString?: string) => {
    if (!dateString) return t('whatsapp.manager.added');
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return t('whatsapp.manager.added');
    }
  };

  const togglePhoneExpansion = (index: number) => {
    if (expandedPhoneIndex === index) {
      setExpandedPhoneIndex(null);
    } else {
      setExpandedPhoneIndex(index);
    }
  };

  const formatShortNumber = (phone: string | undefined) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length <= 8) return phone;
    return `${cleaned.slice(0, 3)}...${cleaned.slice(-3)}`;
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex-1">
          <h2 className="text-xl md:text-2xl font-bold text-[#0A0C0B] mb-1 md:mb-2">{t('whatsapp.manager.title')}</h2>
          <p className="text-sm md:text-base text-[#687d76]">{t('whatsapp.manager.subtitle')}</p>
        </div>
      </div>

      {/* Dedicated WhatsApp Number - Mobile Optimized */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 md:p-3 bg-green-100 text-green-600 rounded-full">
              <Smartphone className="w-5 h-5 md:w-6 md:h-6" />
            </div>
            <div>
              <h3 className="font-semibold text-green-900 text-sm md:text-base">{t('whatsapp.manager.dedicated_whatsapp_number')}</h3>
              <p className="text-xs md:text-sm text-green-700">
                {t('whatsapp.manager.all_registered_send_messages')}
              </p>
            </div>
          </div>
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="text-left md:text-right">
              <p className="font-mono text-base md:text-lg font-bold text-green-900 break-all">{whatsappDedicatedNumber}</p>
              <p className="text-xs md:text-sm text-green-600">{t('whatsapp.manager.for_menu_updates_only')}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={copyDedicatedNumber}
                className="p-2 text-green-700 hover:text-green-900 hover:bg-green-100 rounded-lg"
                aria-label={t('whatsapp.manager.copy_number')}
              >
                <Copy className="w-4 h-4 md:w-5 md:h-5" />
              </button>
              <button
                onClick={() => openWhatsAppChat(whatsappDedicatedNumber)}
                className="p-2 bg-green-600 text-white hover:bg-green-700 rounded-lg"
                aria-label={t('whatsapp.manager.open_whatsapp_chat')}
              >
                <MessageSquare className="w-4 h-4 md:w-5 md:h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add New Phone Number - Mobile Optimized */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6">
        <h3 className="text-base md:text-lg font-semibold text-[#0A0C0B] mb-4">{t('whatsapp.manager.add_phone_number')}</h3>
        
        <div className="space-y-3 md:space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-[#0A0C0B] mb-2">
                {t('whatsapp.manager.country_code')}
              </label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-[#687d76]" />
                <select
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent appearance-none bg-white text-sm md:text-base"
                >
                  {COUNTRY_CODES.map(country => (
                    <option key={country.code} value={country.code}>
                      {country.flag} {country.code}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#0A0C0B] mb-2">
                {t('whatsapp.manager.phone_number')}
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-[#687d76]" />
                <input
                  type="tel"
                  value={newPhoneNumber}
                  onChange={(e) => setNewPhoneNumber(e.target.value)}
                  placeholder={t('whatsapp.manager.phone_number_placeholder')}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent text-sm md:text-base"
                  inputMode="numeric"
                />
              </div>
              <p className="text-xs text-[#687d76] mt-1">
                {t('whatsapp.manager.phone_number_without_code')}
              </p>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row md:items-center gap-3 pt-2">
            <button
              onClick={handleAddPhoneNumber}
              disabled={sendingCode || !newPhoneNumber.trim()}
              className="w-full px-4 py-3 bg-[#0A0C0B] text-white rounded-lg font-medium hover:bg-[#1A1C1B] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm md:text-base"
            >
              {sendingCode ? (
                <>
                  <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin" />
                  <span>{t('whatsapp.manager.adding')}</span>
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 md:w-5 md:h-5" />
                  <span>{t('whatsapp.manager.add_number')}</span>
                </>
              )}
            </button>
          </div>
        </div>
        
        <p className="text-xs md:text-sm text-[#687d76] mt-3">
          {t('whatsapp.manager.verification_code_will_be_sent')}
        </p>
      </div>

      {/* Verification Modal - Mobile Optimized */}
      {selectedPhone && !selectedPhone.verified && (
        <div className="bg-white border border-yellow-200 rounded-lg p-4 md:p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 md:w-5 md:h-5 text-yellow-600" />
                <h3 className="text-base md:text-lg font-semibold text-[#0A0C0B]">{t('whatsapp.manager.verify_phone_number')}</h3>
              </div>
              <p className="text-xs md:text-sm text-[#687d76]">
                {t('whatsapp.manager.enter_6_digit_code_sent')} {renderPhoneNumber(selectedPhone)}
              </p>
            </div>
            <button
              onClick={() => {
                setSelectedPhone(null);
                setVerificationCode('');
              }}
              className="text-[#687d76] hover:text-[#0A0C0B] ml-2"
            >
              <X className="w-4 h-4 md:w-5 md:h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#0A0C0B] mb-2">
                {t('whatsapp.manager.verification_code')}
              </label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                maxLength={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent text-center text-xl md:text-2xl tracking-widest"
                inputMode="numeric"
              />
              <p className="text-xs text-[#687d76] mt-2 text-center">
                {t('whatsapp.manager.code_expires_10_minutes')}
              </p>
            </div>

            <div className="flex flex-col md:flex-row gap-2 md:gap-3">
              <button
                onClick={handleVerifyCode}
                disabled={verifying || verificationCode.length !== 6}
                className="flex-1 px-4 py-3 bg-[#0A0C0B] text-white rounded-lg font-medium hover:bg-[#1A1C1B] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm md:text-base"
              >
                {verifying ? (
                  <>
                    <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin" />
                    <span>{t('whatsapp.manager.verifying')}</span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 md:w-5 md:h-5" />
                    <span>{t('whatsapp.manager.verify_number')}</span>
                  </>
                )}
              </button>
              <button
                onClick={() => handleResendCode(selectedPhone.fullNumber || selectedPhone.phoneNumber || '')}
                disabled={sendingCode}
                className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
              >
                {sendingCode ? (
                  <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin mx-auto" />
                ) : (
                  t('whatsapp.manager.resend_code')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phone Numbers List - Mobile Optimized */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base md:text-lg font-semibold text-[#0A0C0B]">{t('whatsapp.manager.registered_numbers')}</h3>
          <div className="text-xs md:text-sm text-[#687d76]">
            {phoneNumbers.length} number{phoneNumbers.length !== 1 ? 's' : ''}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8 md:py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#7BD5B5]" />
          </div>
        ) : phoneNumbers.length === 0 ? (
          <div className="text-center py-8 md:py-12">
            <MessageSquare className="w-10 h-10 md:w-12 md:h-12 text-[#7BD5B5]/30 mx-auto mb-3 md:mb-4" />
            <p className="text-sm md:text-base text-[#687d76]">{t('whatsapp.manager.no_numbers_registered')}</p>
            <p className="text-xs md:text-sm text-[#687d76] mt-1">
              {t('whatsapp.manager.add_number_above')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {phoneNumbers.map((phone, index) => {
              const isExpanded = expandedPhoneIndex === index;
              const displayNumber = renderPhoneNumber(phone);
              
              return (
                <div
                  key={phone.id || phone.fullNumber || phone.phoneNumber}
                  className={`p-3 md:p-4 border rounded-lg ${phone.isPrimary ? 'border-green-200 bg-green-50' : 'border-gray-200 hover:bg-gray-50'}`}
                >
                  {/* Mobile: Compact View */}
                  <div className="md:hidden">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          phone.verified 
                            ? phone.isPrimary ? 'bg-green-100' : 'bg-blue-100' 
                            : 'bg-yellow-100'
                        }`}>
                          {phone.verified ? (
                            phone.isPrimary ? (
                              <Star className="w-4 h-4 text-green-600" />
                            ) : (
                              <Check className="w-4 h-4 text-blue-600" />
                            )
                          ) : (
                            <AlertCircle className="w-4 h-4 text-yellow-600" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1 flex-wrap">
                            <p className="font-medium text-sm text-[#0A0C0B] truncate">
                              {displayNumber.length > 20 ? formatShortNumber(phone.fullNumber) : displayNumber}
                            </p>
                            {phone.isPrimary && (
                              <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-800 rounded-full whitespace-nowrap">
                                Primary
                              </span>
                            )}
                            {!phone.verified && (
                              <span className="px-1.5 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full whitespace-nowrap">
                                Unverified
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-[#687d76]">
                            Added {renderDate(phone.createdAt)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => togglePhoneExpansion(index)}
                          className="p-1 text-gray-500 hover:text-gray-700"
                        >
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {/* Expanded Content for Mobile */}
                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-[#687d76]">{t('whatsapp.manager.full_number')}</span>
                            <span className="font-mono text-sm text-[#0A0C0B]">{displayNumber}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-[#687d76]">{t('whatsapp.manager.status')}</span>
                            <span className={`text-sm font-medium ${phone.verified ? 'text-green-600' : 'text-yellow-600'}`}>
                              {phone.verified ? t('whatsapp.manager.verified') : t('whatsapp.manager.unverified')}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex gap-2 pt-2">
                          {!phone.verified ? (
                            <button
                              onClick={() => {
                                setSelectedPhone(phone);
                                setVerificationCode('');
                              }}
                              className="flex-1 px-3 py-2 text-sm bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 flex items-center justify-center gap-1"
                            >
                              <Shield className="w-3 h-3" />
                              {t('whatsapp.manager.verify')}
                            </button>
                          ) : !phone.isPrimary ? (
                            <button
                              onClick={() => setAsPrimary(phone.fullNumber || phone.phoneNumber || '')}
                              className="flex-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-1"
                            >
                              <StarOff className="w-3 h-3" />
                              {t('whatsapp.manager.set_as_primary')}
                            </button>
                          ) : null}
                          
                          <button
                            onClick={() => copyToClipboard(phone.fullNumber || phone.phoneNumber || '', t('whatsapp.manager.phone_number_copied'))}
                            className="p-2 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg"
                            title={t('whatsapp.manager.copy_phone_number')}
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          
                          <button
                            onClick={() => openWhatsAppChat(phone.fullNumber || phone.phoneNumber)}
                            className="p-2 text-green-600 hover:text-green-800 border border-green-300 rounded-lg"
                            title={t('whatsapp.manager.open_whatsapp')}
                          >
                            <MessageSquare className="w-4 h-4" />
                          </button>
                        </div>
                        
                        <div className="flex justify-between pt-2">
                          <button
                            onClick={() => handleDeletePhoneNumber(phone.fullNumber || phone.phoneNumber || '')}
                            className="px-3 py-2 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" />
                            {t('whatsapp.manager.remove')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Desktop: Full View */}
                  <div className="hidden md:flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        phone.verified 
                          ? phone.isPrimary ? 'bg-green-100' : 'bg-blue-100' 
                          : 'bg-yellow-100'
                      }`}>
                        {phone.verified ? (
                          phone.isPrimary ? (
                            <Star className="w-5 h-5 text-green-600" />
                          ) : (
                            <Check className="w-5 h-5 text-blue-600" />
                          )
                        ) : (
                          <AlertCircle className="w-5 h-5 text-yellow-600" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-[#0A0C0B]">{displayNumber}</p>
                          {phone.isPrimary && (
                            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                              {t('whatsapp.manager.primary')}
                            </span>
                          )}
                          {!phone.verified && (
                            <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                              Unverified
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[#687d76]">
                          Added {renderDate(phone.createdAt)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!phone.verified ? (
                        <button
                          onClick={() => {
                            setSelectedPhone(phone);
                            setVerificationCode('');
                          }}
                          className="px-3 py-1 text-sm bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 flex items-center gap-1"
                        >
                          <Shield className="w-3 h-3" />
                          {t('whatsapp.manager.verify')}
                        </button>
                      ) : !phone.isPrimary ? (
                        <button
                          onClick={() => setAsPrimary(phone.fullNumber || phone.phoneNumber || '')}
                          className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-1"
                        >
                          <StarOff className="w-3 h-3" />
                          {t('whatsapp.manager.set_as_primary')}
                        </button>
                      ) : null}
                      
                      <button
                        onClick={() => copyToClipboard(phone.fullNumber || phone.phoneNumber || '', t('whatsapp.manager.phone_number_copied'))}
                        className="p-2 text-gray-500 hover:text-gray-700"
                        title={t('whatsapp.manager.copy_phone_number')}
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => openWhatsAppChat(phone.fullNumber || phone.phoneNumber)}
                        className="p-2 text-green-600 hover:text-green-800"
                        title={t('whatsapp.manager.open_whatsapp')}
                      >
                        <MessageSquare className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => handleDeletePhoneNumber(phone.fullNumber || phone.phoneNumber || '')}
                        className="p-2 text-red-600 hover:text-red-800"
                        title={t('whatsapp.manager.remove_number')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* How It Works Section - Mobile Optimized */}
   
    </div>
  );
};

export default WhatsAppManager;